// ---------- dictionnaire espagnol ----------
// Copie conforme de fr.ts, clé pour clé — typé `Record<I18nKey, string>` ci-dessous, donc une clé
// manquante ou en trop est une ERREUR DE COMPILATION, jamais un texte français qui atteindrait la
// production. Voir fr.ts pour les conventions suivies ici (clés plates, `{{var}}` portant la
// phrase entière, les trois registres de shared/ traités à part dans ./index.ts).
//
// Tutoiement (« tú »), comme le français et l'anglais : le jeu s'adresse à quelqu'un qui colorie,
// pas à un client.
import type { I18nKey } from './fr';

const es: Record<I18nKey, string> = {
  // ---------- marque (index.html, p.html, splash.ts) ----------
  'brand.name': 'Codigrid',

  // ---------- index.html <head> ----------
  'app.title': 'Codigrid — cuadrícula de motivos',
  'app.description':
    'Un juego de colorear lógico: una imagen se convierte en una cuadrícula de símbolos codificados.',

  // ---------- commun (plusieurs dialogues) ----------
  'common.close': 'Cerrar',
  'common.cancel': 'Cancelar',
  'common.back': 'Volver',

  // ---------- home.ts / index.html #home ----------
  'home.filter.doing': 'En curso',
  'home.filter.done': 'Terminados',
  'home.tab.feed': 'Mi muro',
  'home.tab.daily': 'Diario',
  'home.tab.library': 'Biblioteca',
  'home.tab.settings': 'Ajustes',
  'home.tab.create': 'Crear',
  'home.tab.new': 'Nuevo',
  'home.category.celebrite': 'Famosos',
  'home.category.personnes': 'Retratos',
  'home.category.monuments': 'Monumentos',
  'home.category.scenes': 'Escenas de la vida',
  'home.category.contes': 'Cuentos y leyendas',
  'home.category.saisonnier': 'De temporada',
  'home.category.paysage': 'Paisajes',
  'home.category.animaux': 'Animales',
  'home.category.vehicules': 'Vehículos',
  'home.category.sport': 'Deporte',
  'home.category.art': 'Arte',
  'home.category.divers': 'Varios',
  'home.category.other': 'Otros',
  'home.facet.category.title': 'Categoría',
  'home.facet.category.all': 'Todas',
  'home.facet.difficulty.title': 'Dificultad',
  'home.facet.difficulty.all': 'Todas',
  'home.facet.alphabet.title': 'Alfabeto',
  'home.facet.alphabet.all': 'Todos',
  // « casillas » reste invariable ici, comme en français : la plus petite grille fait 4 × 4, donc
  // le singulier ne s'affiche jamais.
  'home.card.tooltip': '{{w}} × {{h}} casillas · {{diff}}',
  'home.card.new': 'Nuevo',
  // ---------- la duración anunciada en una tarjeta (home.ts, shared/effort.ts) ----------
  'home.card.eta': '≈ {{d}}',
  'home.card.etaLong': 'unos {{d}} en total',
  'home.card.leftLong': 'quedan unos {{d}}',
  'time.unit.min': 'min',
  'time.unit.h': 'h',
  'time.min': '{{n}} min',
  'time.h': '{{n}} h',
  'time.hmin': '{{h}} h {{m}}',
  'time.hplus': '{{n}} h+',
  'home.empty.noMatch': 'Ningún puzle coincide con estos filtros todavía.',
  'home.empty.resetFilters': 'Quitar los filtros',
  'home.empty.libraryDone': 'Has empezado todos los puzles disponibles. Vuelve: llegarán más.',
  'home.empty.libraryDonePacks': 'Has empezado todos tus puzles. Te esperan packs nuevos.',
  'home.empty.seePacks': 'Ver los packs',
  'home.empty.puzzleGone': 'Este puzle no está disponible.',
  'home.empty.retry': 'Reintentar',
  'home.empty.noneDoing': 'Ningún puzle en curso. Elige uno en la biblioteca.',
  'home.empty.openLibrary': 'Abrir la biblioteca',
  'home.empty.noneDone': 'Nada terminado todavía. Ya llegará.',
  'home.empty.noPuzzles': 'Ningún puzle disponible todavía.',
  'home.empty.serverError': 'No se ha podido contactar con el servidor.',
  'home.loading': 'Cargando…',
  // ---------- la tarjeta de valoración del flujo (home.ts::rateCard, reglas en review.ts) ----------
  'home.rate.1.title': 'Ya conoces bien Codigrid',
  'home.rate.1.body':
    'Tu opinión en Play Store ayuda a otros aficionados a colorear a saber si el juego es para ellos.',
  'home.rate.1.cta': 'Compartir mi opinión',
  'home.rate.2.title': '¿Te lo pasas bien con Codigrid?',
  'home.rate.2.body': 'Dilo en 10 segundos en Play Store: para un juego pequeño, lo cambia todo.',
  'home.rate.2.cta': 'Valorar Codigrid',
  'home.rate.3.title': '¿Quieres nuevos puzles?',
  'home.rate.3.body':
    'Cada valoración en Play Store cuenta: es lo que me permite seguir creándolos.',
  'home.rate.3.cta': 'Dejar mi valoración',
  'home.progression.open': 'Progreso',
  'home.packsBanner.sub': '{{n}} packs por desbloquear',
  'home.packsBanner.one': '1 pack por desbloquear',
  'home.packsBanner.cta': 'Descubrir',

  // ---------- creaciones: home.ts (tarjeta, filtro, tarjetas) y creer.ts (la herramienta) ----------
  'home.create.title': 'Crear mi puzle',
  'home.create.sub': 'A partir de una de tus fotos',
  'home.facet.mine': 'Mis creaciones',
  'home.pack.base': 'Para empezar',
  'home.card.mine': 'Mi creación',
  'home.empty.noCreations': 'Todavía no has creado ningún puzle. Tus creaciones aparecerán aquí.',
  'home.creation.delete': 'Borrar esta creación',
  'home.creation.deleteConfirm': '¿Borrar «{{title}}»? Se perderán el puzle y tu progreso en él.',
  'creer.step': '{{n}} / {{total}}',
  'creer.next': 'Continuar',
  'creer.busy': 'Preparando la hoja…',
  'creer.photo.title': 'Tu foto',
  'creer.photo.camera': 'Hacer una foto',
  'creer.photo.gallery': 'Mi galería',
  'creer.photo.tipsTitle': 'Las fotos que dan los puzles más bonitos',
  'creer.photo.tip1': 'Un motivo que llene el encuadre',
  'creer.photo.tip1Sub': 'Una cara, un animal, una silueta, vistos de cerca.',
  'creer.photo.tip2': 'Contraste',
  'creer.photo.tip2Sub': 'Claros y oscuros bien marcados: son ellos los que dibujan.',
  'creer.photo.tip3': 'Una foto nítida, con buena luz',
  'creer.photo.tip3Sub': 'Luz de día, sin contraluz ni desenfoque.',
  'creer.photo.tip4': 'Nada de detalles diminutos',
  'creer.photo.tip4Sub': 'Un texto, una multitud o un paisaje lejano se pierden en las casillas.',
  'creer.photo.cutTitle': '¿Un retrato? Quita el fondo primero',
  'creer.photo.cutText':
    'Con una herramienta como {{link}}, por ejemplo; luego elige la imagen en Mi galería: el fondo se vuelve papel.',
  'creer.photo.private':
    '<b>Tu foto nunca se envía.</b> La cuadrícula se fabrica en tu móvil, y solo tú puedes ver el puzle.',
  'creer.photo.error': 'No se puede leer esta imagen. Prueba con otra foto.',
  'creer.crop.title': 'Encuadrar',
  'creer.crop.hint': 'Pellizca para hacer zoom, desliza para mover',
  'creer.crop.photo': 'Foto',
  'creer.crop.square': 'Cuadrado',
  'creer.crop.portrait': 'Vertical',
  'creer.crop.landscape': 'Horizontal',
  'creer.crop.rotate': 'Girar un cuarto de vuelta',
  'creer.adjust.title': 'Tu hoja',
  'creer.tool.tech': 'Técnica',
  'creer.tool.detail': 'Detalle',
  'creer.tool.light': 'Luz',
  'creer.light.normal': 'Normal',
  'creer.light.clear': 'Claro',
  'creer.light.notch': 'Luz {{n}} de {{total}}',
  'creer.tool.contrast': 'Contraste',
  'creer.contrast.soft': 'Natural',
  'creer.contrast.strong': 'Marcado',
  'creer.contrast.notch': 'Contraste {{n}} de {{total}}',
  'creer.zoomReset': 'Ver la vista previa entera',
  'creer.detail.cells': 'Número de casillas',
  'creer.detail.readout': '{{w}} × {{h}} · {{n}} casillas',
  'creer.detail.notch': '{{n}} casillas de lado',
  'creer.detail.less': 'Rápido de pintar',
  'creer.detail.more': 'Mucho detalle',
  'creer.ready.title': 'Resumen',
  'creer.ready.name': 'Su nombre',
  'creer.ready.placeholder': 'Mi puzle',
  'creer.ready.photo':
    '<b>Tu foto se queda en tu móvil.</b> Nunca se envía, y el puzle que sale de ella tampoco.',
  'creer.ready.private':
    '<b>Este puzle es solo tuyo, en este móvil.</b> No se guarda en ningún otro sitio: si desinstalas el juego o cambias de móvil, no te sigue.',
  'creer.ready.go': 'Guardar el puzle',
  'creer.ready.saving': 'Guardando…',
  'creer.ready.storeFailed':
    'No se ha podido guardar el puzle en este móvil. Libera un poco de espacio y vuelve a intentarlo.',
  'creer.ready.tooMany':
    'Ya tienes {{n}} creaciones, el máximo. Borra una desde «Mis creaciones» para hacer otra.',
  'creer.ready.full':
    'La creación de puzles no está disponible por el momento. Inténtalo más tarde.',
  'creer.ready.freeLeftMany': 'Te quedan {{n}} creaciones gratis, sin anuncios.',
  'creer.ready.freeLeftOne': 'Te queda 1 creación gratis, sin anuncios.',
  'creer.ready.adNote':
    'Tu primera creación era gratis. Esta se guarda después de un anuncio corto: es lo que mantiene vivo el juego.',
  'creer.ready.goAd': 'Ver un anuncio y guardar',
  'creer.ready.adSkipped':
    'Anuncio interrumpido: tu creación aún no se ha guardado. Vuelve a intentarlo cuando quieras.',
  'creer.ready.adUnavailable':
    'No hay ningún anuncio disponible ahora. Vuelve a intentarlo en un momento.',

  // ---------- daily.ts / index.html #dailyPage (el puzle del día) ----------
  'daily.month.prev': 'Mes anterior',
  'daily.month.next': 'Mes siguiente',
  'daily.state.todo': 'Por pintar',
  'daily.state.doing': 'En curso, {{pct}} %',
  'daily.state.done': 'Terminado',
  'daily.cell.aria': '{{date}} — {{state}}',
  'daily.empty.stock': 'Hoy no hay puzle. Vuelve mañana.',
  'daily.empty.update': 'Este puzle necesita una versión más reciente de la aplicación.',
  'daily.today.kicker': 'La hoja de hoy',
  'daily.today.unknown': 'Nadie sabe todavía qué es',
  'daily.today.paint': 'Pintarla',
  'daily.today.resume': 'Continuar',
  'daily.today.see': 'Verla otra vez',

  // ---------- packs.ts / index.html #packs (packs de puzles desbloqueables) ----------
  'packs.title': 'Packs disponibles',
  'packs.carousel': 'carrusel',
  'packs.loading': 'Cargando…',
  'packs.empty': 'Nada que desbloquear todavía. Vuelve: llegará más.',
  'packs.puzzlesLabel.one': 'puzle',
  'packs.puzzlesLabel.other': 'puzles',
  'packs.selection': 'Selección',
  'packs.unlock': 'Desbloquear',
  'packs.watchAd': 'Ver un anuncio',
  // ---------- les arguments d'un pack (src/packArgument.ts) ----------
  'packs.arg.debutant': 'Ideal para empezar',
  'packs.arg.portraits': 'Rostros',
  'packs.arg.patience': 'Tómate tu tiempo',
  'packs.arg.nouveau': 'Recién llegado',
  'packs.arg.costaud': 'Cuadrículas grandes',
  'packs.arg.express': 'Partidas cortas',
  'packs.arg.mono': 'Una sola técnica',
  'packs.arg.varie': 'Técnicas variadas',
  'packs.arg.couleur': 'En color',
  'packs.unlocking': 'Desbloqueando…',
  // Sous le bouton Desbloquear : dit qu'il n'y a pas d'argent en jeu — cf. src/packs.ts,
  // unlockNote(). Le `<b>` est du balisage voulu, même précédent que home.intro.
  'packs.adNote': '<b>Gratis</b> · unos 30 s',
  'packs.freeNote': '<b>Gratis</b>, con un toque',
  'packs.adLoading': 'Cargando el anuncio…',
  'packs.adUnavailable': 'No hay ningún anuncio disponible: inténtalo de nuevo.',
  'packs.adSkipped': 'El anuncio no ha terminado.',
  'packs.adFailed': 'No ha funcionado: inténtalo de nuevo.',
  'packs.reveal.title.one': '¡{{n}} puzle desbloqueado!',
  'packs.reveal.title.other': '¡{{n}} puzles desbloqueados!',
  'packs.reveal.sub': '«{{title}}» se ha unido a tu Biblioteca',
  'packs.reveal.continue': 'Continuar',
  'packs.feature.coeur': 'Nuestro favorito',
  'packs.feature.saison': 'De temporada',
  'packs.feature.nouveau': 'Nuevo',
  'packs.feature.evenement': 'Evento especial',
  'packs.feature.joueurs': 'Favorito de los jugadores',
  'packs.feature.populaire': 'El más desbloqueado',
  'packs.volume': '{{title}} n.º {{n}}',
  'packs.intro': '{{n}} puzles por descubrir',
  'packs.shelf.n': '{{n}} packs',
  'packs.unlocks': 'Desbloqueado por {{n}} jugadores',
  'packs.fact.time': 'para colorear',
  'packs.fact.votes': '{{n}} valoraciones',
  'packs.fact.unrated': 'aún sin valorar',
  'packs.inside': 'Qué contiene',
  'packs.inside.hint': 'Cada tema se descubre al colorear.',
  'packs.sheet.puzzle': 'Puzle {{n}}',
  'technique.family.motifs': 'Patrones',
  'technique.family.hachures': 'Tramado',
  'technique.family.ascii': 'ASCII',
  'technique.family.pointille': 'Puntillismo',
  'technique.family.des': 'Dados',
  'technique.family.briques': 'Ladrillos',
  'technique.family.peinture': 'Pintura',
  'technique.family.croix': 'Bordado',

  // ---------- progression.ts / index.html #progression (insignias + Juntos) ----------
  'progression.title': 'Progreso',
  'progression.tier.bronze': 'Bronce',
  'progression.tier.argent': 'Plata',
  'progression.tier.or': 'Oro',
  'progression.tier.platine': 'Platino',
  'progression.loading': 'Cargando…',
  'progression.needAccount': 'Termina un puzle para ver qué pintan los demás jugadores.',
  'progression.loadFailed': 'No se ha podido contactar con el servidor.',
  // L’écran « Moi » et ses badges (src/profile.ts) — les règles, seuils compris, viennent du serveur.
  'progression.tab.me': 'Yo',
  // L’onglet « Ensemble » (src/together.ts) et la rareté des badges — ce qui a remplacé la ligue.
  'progression.tab.together': 'Juntos',
  'me.rarity': '{{pct}} % de los jugadores',
  'me.rarity.rare': 'Raro · {{pct}} % de los jugadores',
  'me.rarity.locked': 'Ya lo tiene el {{pct}} % de los jugadores',
  'together.eyebrow': 'Esta semana · desde el lunes',
  'together.sheets.one': 'puzle terminado',
  'together.sheets.other': 'puzles terminados',
  'together.players.one': 'por {{n}} jugador',
  'together.players.other': 'por {{n}} jugadores',
  'together.stats': 'La semana en cifras',
  'together.trend.eyebrow': 'Tendencia',
  'together.trend.up': 'respecto a la semana pasada',
  'together.trend.same': 'como la semana pasada',
  'together.trend.calm': 'un poco más tranquila que la semana pasada',
  'together.trend.ctx': '{{n}} puzles, frente a {{last}} a la misma hora la semana pasada.',
  'together.peak.eyebrow': 'Hora punta',
  'together.peak.what': 'la hora en que más se colorea',
  'together.crossed.eyebrow': 'Insignias',
  'together.crossed.one': 'nivel alcanzado desde el lunes',
  'together.crossed.other': 'niveles alcanzados desde el lunes',
  'together.joined.eyebrow': 'Bienvenida',
  'together.joined.one': 'jugador nuevo esta semana',
  'together.joined.other': 'jugadores nuevos esta semana',
  'together.joined.ctx': 'La semana pasada a la misma hora: {{last}}.',
  'together.empty':
    'Nadie ha terminado todavía un puzle esta semana. El primero puede ser el tuyo.',
  'together.mine.title': 'Tu parte de la semana.',
  'together.mine.zero': 'Aún ningún puzle esta semana. Sin prisa.',
  'together.mine.one': '1 puzle terminado de {{total}}.',
  'together.mine.other': '{{n}} puzles terminados de {{total}}.',
  'together.daily.one': '{{n}} jugador ya ha pintado el puzle del día.',
  'together.daily.other': '{{n}} jugadores ya han pintado el puzle del día.',
  'together.daily.with': 'Contigo, {{n}} jugadores han pintado el puzle del día.',
  'together.daily.go': 'Pintarlo',
  'progression.tier.prisme': 'Prisma',
  'me.eyebrow': 'Tu apodo',
  'me.edit': 'Cambiar de apodo',
  'me.choose': 'Elige tu apodo',
  'me.since': 'Juegas desde el {{date}}',
  'me.days.one': '{{n}} día',
  'me.days.other': '{{n}} días',
  'me.numbers': 'Tus cifras',
  'me.cells.eyebrow': 'Casillas pintadas',
  'me.cells.what': 'desde que llegaste',
  'me.fav.eyebrow': 'Tu estilo',
  'me.fav.cat': 'Categoría favorita: {{cat}}',
  'me.biggest.eyebrow': 'Tu puzle más grande',
  'me.biggest.cells': '{{n}} casillas pintadas',
  'me.biggest.untitled': 'Sin título',
  'me.activity.title': 'Tus últimas 12 semanas',
  'me.activity.more': 'más',
  'me.activity.less': 'menos',
  'me.activity.today': 'hoy',
  'me.activity.ago': 'hace 12 semanas',
  'me.activity.how': 'Una casilla por día: cuanto más oscura, más puzles terminaste ese día.',
  'me.activity.one': '1 día con al menos un puzle terminado',
  'me.activity.other': '{{n}} días con al menos un puzle terminado',
  'me.fav.none': 'Por descubrir',
  'me.fav.noneHint': 'Termina un puzle para descubrirlo',
  'me.activity.none': 'Aún no hay puzles terminados: cada uno oscurecerá la casilla de su día.',
  'together.cells.eyebrow': 'Casillas pintadas',
  'together.cells.what': 'por todos, desde el lunes',
  'together.best.eyebrow': 'Mejor día',
  'together.best.one': '{{n}} puzle terminado',
  'together.best.other': '{{n}} puzles terminados',
  'together.top.eyebrow': 'El mejor valorado de la semana',
  'together.top.votes.one': '{{n}} valoración',
  'together.top.votes.other': '{{n}} valoraciones',
  'together.top.done': 'Ya lo pintaste',
  'together.top.locked': 'En un pack por desbloquear',
  'together.top.unlock': 'Desbloquear el pack',
  'me.stat.finished': 'puzles terminados',
  'me.stat.daily': 'puzles del día',
  'me.stat.styles': 'estilos jugados',
  'me.tally': 'niveles desbloqueados',
  'me.section.milestones': 'Hitos',
  'me.section.families': 'Alfabetos',
  'me.section.categories': 'Categorías',
  'me.empty': 'Termina un puzle para desbloquear tus primeras insignias.',
  'badge.library': 'Biblioteca',
  'badge.daily': 'Puzle del día',
  'badge.share': 'Compartir',
  'badge.family.dots': 'Puntillismo',
  'badge.family.shapes': 'Formas',
  'badge.family.hatching': 'Tramado',
  'badge.family.dice': 'Dados',
  'badge.family.ascii': 'ASCII',
  'badge.family.bricks': 'Ladrillos',
  'badge.family.paint': 'Pintura',
  'badge.family.embroidery': 'Punto de cruz',
  'badge.goal.library':
    'Termina puzles de la biblioteca. Los puzles del día tienen su propia insignia.',
  'badge.goal.daily':
    'Termina puzles del día. No hace falta jugar días seguidos: un día recuperado desde el calendario también cuenta.',
  'badge.goal.share':
    'Comparte tus dibujos terminados. Cada hoja cuenta una sola vez, aunque la compartas varias veces.',
  'badge.goal.family': 'Termina puzles en {{alphabets}}.',
  'badge.goal.category': 'Termina puzles de la categoría {{category}}.',
  'badge.unit.puzzles.one': '{{n}} puzle',
  'badge.unit.puzzles.other': '{{n}} puzles',
  'badge.unit.shares.one': '{{n}} hoja',
  'badge.unit.shares.other': '{{n}} hojas',
  'badge.next.bronze': 'para el Bronce',
  'badge.next.argent': 'para la Plata',
  'badge.next.or': 'para el Oro',
  'badge.next.platine': 'para el Platino',
  'badge.next.prisme': 'para el Prisma',
  'badge.reached': 'Nivel {{tier}} alcanzado',
  'badge.earned': 'Conseguido',
  'badge.locked': 'Por desbloquear',

  'celebrate.eyebrow': 'Nuevo nivel',
  'celebrate.next': 'Siguiente',
  'celebrate.later': 'Más tarde',
  'celebrate.see': 'Ver mis insignias',
  'celebrate.skipped.one': '{{tiers}} superado por el camino',
  'celebrate.skipped.other': '{{tiers}} superados por el camino',
  'celebrate.more.eyebrow': 'Y además',
  'celebrate.more.title.one': '{{n}} nivel más',
  'celebrate.more.title.other': '{{n}} niveles más',
  'celebrate.more.line': 'Las encontrarás todas en tus insignias.',
  'celebrate.welcome.eyebrow': 'Novedad: insignias',
  'celebrate.welcome.title': 'Tus insignias han llegado',
  'celebrate.welcome.count': 'niveles ya conseguidos',
  'celebrate.welcome.line': 'Cuentan todo lo que has coloreado hasta ahora.',

  // ---------- prefs.ts / la page Réglages (index.html #prefsPage) ----------
  'prefs.lang.label': 'Idioma',
  'prefs.lang.hint': 'Cambia todos los textos de la aplicación',
  'prefs.skin.label': 'Tema',
  'prefs.skin.hint': 'Cómo se viste la pantalla; la hoja no cambia',
  'prefs.sound.label': 'Sonido',
  'prefs.sound.hint': 'Una nota por casilla rellenada',
  'prefs.haptics.label': 'Vibración',
  'prefs.haptics.hint': 'Un toque en la mano en cada casilla',
  'prefs.motion.label': 'Animaciones',
  'prefs.motion.hint': 'Casillas que se abren, partículas, sacudidas',
  'prefs.compass.label': 'Brújula',
  'prefs.compass.hint': 'Una flecha hacia el símbolo cuando sale de la pantalla',
  'prefs.push.label': 'Notificaciones',
  'prefs.push.hint': 'Puzles nuevos, recordatorios',
  'prefs.inks.label': 'Color de las casillas',
  'prefs.inks.hint': 'Con qué se rellena la hoja',
  'prefs.marks.label': 'Marcador de casillas',
  'prefs.marks.hint': 'Cómo se señalan las casillas que faltan',
  'prefs.markHues.label': 'Color del marcador',
  'prefs.markHues.hint': 'Solo afecta al marcador, no a la tinta',
  'prefs.username.label': 'Apodo',
  'prefs.username.hint': 'Visible si algún día quieres enseñarlo',
  'prefs.username.placeholder': 'Elige un apodo',
  'prefs.username.random': 'Proponme un apodo',
  'prefs.username.save': 'Guardar',
  'prefs.username.saving': 'Guardando…',
  'prefs.username.taken': 'Ese apodo ya está cogido.',
  'prefs.username.rejected': 'Este apodo no se puede usar. Elige otro.',
  'prefs.username.failed': 'No ha funcionado: inténtalo con conexión.',
  // ---------- el código de recuperación (prefs.ts) ----------
  'prefs.recovery.label': 'Recuperar mi progreso',
  'prefs.recovery.hint': 'Un código para guardar y seguir en otro móvil',
  'prefs.recovery.mine':
    'Apunta este código en algún sitio. Devuelve tu progreso en otro dispositivo, o después de reinstalar.',
  'prefs.recovery.loading': 'Un momento…',
  'prefs.recovery.offline': 'Código no disponible: inténtalo con conexión.',
  'prefs.recovery.copy': 'Copiar',
  'prefs.recovery.copied': 'Copiado',
  'prefs.recovery.theirs':
    '¿Ya tienes un código? Escríbelo aquí: este dispositivo retomará ese progreso, y lo que has pintado aquí se conserva.',
  'prefs.recovery.placeholder': 'las palabras separadas por guiones',
  'prefs.recovery.use': 'Restaurar',
  'prefs.recovery.restoring': 'Restaurando…',
  'prefs.recovery.unknown': 'Este código no corresponde a ningún progreso.',
  'prefs.recovery.failed': 'No ha funcionado: inténtalo con conexión.',
  'prefs.recovery.done': 'Progreso recuperado.',
  'maker.eyebrow': 'Unas palabras del creador',
  'maker.title': 'Hola, soy tidou44',
  'maker.who':
    'Soy desarrollador web independiente y he creado Codigrid yo solo, por el gusto de ver nacer un dibujo casilla a casilla.',
  'maker.ask':
    'La publicidad es discreta, y es una elección. Si el juego te gusta, compartirlo o valorarlo me ayuda mucho: tengo muchas ideas para lo que viene.',
  'maker.askWeb':
    'La publicidad es discreta, y es una elección. Si el juego te gusta, compartirlo me ayuda mucho: tengo muchas ideas para lo que viene.',
  'maker.share': 'Compartir el juego',
  'maker.rate': 'Valorar',
  'maker.contact': 'Escríbeme',
  'maker.shareText':
    'Te recomiendo Codigrid, un juego de colorear lógico donde la cuadrícula no dice lo que esconde. Por ahora, solo disponible en Android.',
  'prefs.ads.label': 'Preferencias de anuncios',
  'prefs.ads.hint': 'Revisar tu consentimiento para los anuncios',
  'prefs.privacy.label': 'Privacidad',
  'prefs.privacy.hint': 'Lo que se queda en el dispositivo y lo que se envía',
  'prefs.wipe.label': 'Borrar mis datos',
  'prefs.wipe.hint': 'La cuenta y todas las partidas, aquí y en el servidor',
  'prefs.wipe.confirm': '¿Borrar la cuenta y todas las partidas? No se puede deshacer.',
  'prefs.wipe.progress': 'Borrando…',
  'prefs.wipe.failed': 'No ha funcionado: no se ha borrado nada. Inténtalo con conexión.',

  // ---------- mark.ts (marcador de casillas, solo en el juego — no está en shared/) ----------
  'mark.battant.label': 'Borde grueso',
  'mark.battant.hint': 'Un borde grueso, el dibujo intacto',
  'mark.hachures.label': 'Tramado',
  'mark.hachures.hint': 'Rayas sobre las casillas',
  'mark.cases.label': 'Cruz por casilla',
  'mark.cases.hint': 'Una cruz en el centro de cada casilla',
  'skin.clair': 'Claro',
  'skin.ciel': 'Cielo',
  'skin.soir': 'Noche',

  'mark.hue.citron': 'Lima',
  'mark.hue.ambre': 'Ámbar',
  'mark.hue.rose': 'Rosa',
  'mark.tooltip': '{{label}} — {{hint}}',
  'mark.ariaLabel': '{{label}}. {{hint}}',

  // ---------- coach.ts et gestureDialog.ts : ce que l'écran apprend au début ----------
  'coach.boussole': 'El símbolo sigue por ahí. Toca la flecha.',
  'coach.close': 'Ocultar este consejo',
  'ordre.later':
    'Los colores se aplican <b>en orden</b>: de los claros a los oscuros y, al final, los blancos para los brillos. Este llegará más tarde.',
  'coach.hello.title': '¡Hola, te doy la bienvenida!',
  'coach.hello.body':
    'Soy el zorro de Codigrid. Cada cuadrícula esconde un dibujo: rellena sus casillas y verás cómo aparece.',
  'coach.hello.next': 'Enséñame',
  'coach.dlg.title': 'Dos formas de colorear',
  'coach.dlg.tap': '<b>Toca</b> una casilla resaltada: se rellena toda su zona.',
  'coach.dlg.swipe': '<b>Desliza</b> sin levantar el dedo: se rellena cada zona que cruzas.',
  'coach.dlg.next': 'Siguiente',
  'coach.move.title': 'Para moverte por el dibujo',
  'coach.move.drag':
    '<b>Arrastra</b> la cuadrícula para moverte, empezando por una casilla que no esté resaltada.',
  'coach.move.pinch': '<b>Con dos dedos</b>: sepáralos para acercar, júntalos para alejar.',
  'coach.dlg.go': '¡Vamos!',
  'piece.done.title': '¡Bravo!',
  'piece.done.body': 'Solo era su mirada: el resto del dibujo te espera. Y no es el único.',
  'piece.done.packs': '<b>Cientos de dibujos</b> para descargar, ordenados en packs',
  'piece.done.daily': 'Un <b>dibujo del día</b>, en la pestaña Diario',
  'piece.done.create': 'Tus <b>propios puzles</b>, a partir de tus fotos',
  'piece.done.ask': '¿Terminamos este dibujo juntos, o prefieres ver la biblioteca?',
  'piece.done.finish': 'Terminar este dibujo',
  'piece.done.library': 'Ver la biblioteca',
  'intro.bigDone': '¡Tu primer dibujo está terminado! Cientos más te esperan en la biblioteca.',
  'piece.bravo': '¡Bravo!',
  'piece.line': 'Solo era su mirada. El resto te espera.',
  'prefs.intro.label': 'Repetir la introducción',
  'prefs.intro.hint': 'El primer dibujo, con el zorro',
  'prefs.coach.label': 'Volver a ver los gestos',
  'prefs.coach.hint': 'Colorear, hacer zoom, moverse, en animación',

  // ---------- dialogue de mise à jour (pwa.ts / index.html #update) ----------
  'update.title': 'Actualización disponible',
  'update.body':
    'Hay una versión nueva de Codigrid lista. Se instala al recargar la página: tu progreso se conserva.',
  'update.reload': 'Recargar',
  'update.reloading': 'Actualizando…',

  // ---------- écran de jeu (ui.ts / index.html .app) ----------
  'game.close': 'Cerrar la hoja',
  'game.finished': 'Terminado',
  'game.finishedSub': '{{todo}} casillas · {{w}}×{{h}} · {{alphabet}}',
  // La ligne « Avec toi, … » de la carte de fin (ui.ts, showPeers).
  'game.peers.sheet': 'Contigo, {{n}} jugadores han terminado este dibujo.',
  'game.peers.daily': 'Contigo, {{n}} jugadores han pintado el puzle del día.',
  'game.peers.first': '¡Estás entre los primeros en terminar este dibujo!',
  'game.peers.none': '¡Nadie lo había terminado antes que tú!',
  'game.rate': 'Puntuar este puzle',
  'game.replay': 'Ver la animación',
  'game.share': 'Compartir',
  'game.linkCopied': '✓ Enlace copiado',
  'shareSheet.image': 'Imagen',
  'shareSheet.imageSub': 'El dibujo terminado',
  'shareSheet.video': 'Vídeo',
  'shareSheet.videoSub': 'El relleno, en 10 segundos',
  'shareSheet.imageBusy': 'Preparando la imagen…',
  'shareSheet.videoBusy': 'Grabando tu relleno…',
  'shareSheet.imageReady': '¡Tu imagen está lista!',
  'shareSheet.videoReady': '¡Tu vídeo está listo!',
  'shareSheet.imageSend': 'Enviar la imagen',
  'shareSheet.videoSend': 'Enviar el vídeo',
  'shareSheet.imageSaved': '✓ Imagen guardada',
  'shareSheet.videoSaved': '✓ Vídeo guardado',
  'shareSheet.other': 'Elegir otra cosa',
  'shareSheet.failed': 'No se ha podido preparar el archivo. Inténtalo de nuevo en un momento.',
  'shareSheet.msg.mineVideo':
    'Mi foto se convierte en un dibujo, casilla a casilla. Hecho en Codigrid, gratis en Android:\n{{url}}',
  'shareSheet.msg.mineImage':
    'Mi foto, convertida en un dibujo para colorear en Codigrid. Gratis en Android:\n{{url}}',
  'shareSheet.msg.video':
    'Mira cómo aparece este dibujo casilla a casilla. Codigrid, gratis en Android:\n{{url}}',
  'shareSheet.msg.image': 'Coloreado casilla a casilla en Codigrid. Gratis en Android:\n{{url}}',
  'game.shareText':
    'Acabo de terminar una hoja en Codigrid, un juego de colorear lógico donde la cuadrícula no dice lo que esconde.',

  // ---------- splash.ts ----------
  // Longueur tenue près de l'original — cf. l'avertissement à côté de L1/L2 dans splash.ts :
  // fit() dimensionne le texte du splash sur le nombre exact de caractères de ces deux lignes.
  'splash.line1': 'Casilla a casilla,',
  'splash.line2': 'la imagen se revela',

  // ---------- report.ts (el fallo al iniciar) ----------
  'crash.boot': 'El juego no ha podido iniciarse.',

  // ---------- share.ts (texte injecté dans p.html au chargement) ----------
  'share.title': '«{{title}}», terminado',
  'share.meta': '{{w}} × {{h}} · {{alphabet}}',
  'share.docTitle': '{{title}} — Codigrid',

  // ---------- p.html (le squelette statique de la page de partage) ----------
  'share.page.metaDescription':
    'Codigrid, un juego de colorear lógico: una cuadrícula de símbolos codificados y el dibujo que aparece bajo los dedos.',
  'share.page.docTitleDefault': 'Una hoja terminada — Codigrid',
  'share.page.title': 'Una hoja terminada',
  'share.page.pitch':
    'Codigrid es un juego de colorear lógico. Cada casilla lleva un símbolo codificado, los rellenas de uno en uno y el dibujo aparece bajo tu mano. <em>La cuadrícula no dice lo que esconde.</em>',
  'share.page.play': 'Jugar ahora',
  'share.page.store': 'Disponible en Google Play',
  'share.page.fine': 'Gratis, sin cuenta. Una lámina nueva cada día.',
  'share.page.fail': 'Este dibujo ya no está disponible.',
  'share.page.again': '↻ Ver cómo se rellena',
};

export default es;
